<template>
  <div class="hammershark-root">
    <!-- Controls overlay -->
    <div class="fs-controls">
      <div class="peer-id-display">{{ myPeerId.slice(0, 8) }}</div>
      <button class="btn-fs" @click="toggleAdjust">Adjust</button>
      <button class="btn-fs" @click="handleShareCamera" :class="{ active: !!localStream }">
        {{ localStream ? 'Stop Cam' : 'Share Cam' }}
      </button>
    </div>

    <!-- Split screen -->
    <div class="split-screen">
      <div
        v-for="side in ['left', 'right']"
        :key="side"
        class="panel"
      >
        <!-- Source dropdown -->
        <div class="source-select-wrap">
          <select class="source-select" v-model="state[side].source">
            <option value="">— none —</option>
            <option v-if="localStream" :value="myPeerId">My camera</option>
            <option
              v-for="pid in remotePeerIds"
              :key="pid"
              :value="pid"
            >{{ pid }}</option>
          </select>
        </div>

        <!-- Video -->
        <video
          class="panel-video"
          autoplay
          playsinline
          muted
          :ref="el => setVideoRef(side, el)"
          :style="videoStyle(side)"
        ></video>

        <!-- Click-to-play overlay (shown when autoplay is blocked) -->
        <div
          v-if="state[side].paused"
          class="play-overlay"
          @click="playPanel(side)"
        >
          <span class="play-icon">&#9654;</span>
          <span class="play-label">Click to play</span>
        </div>
      </div>
    </div>

    <!-- Adjustment panel -->
    <div v-if="state.showAdjust" class="adjust-panel">
      <div class="adjust-inner">
        <div
          v-for="side in ['left', 'right']"
          :key="side"
          class="adjust-side"
        >
          <h4>{{ side.charAt(0).toUpperCase() + side.slice(1) }} panel</h4>
          <label>
            Translate X
            <input type="range" min="-200" max="200" v-model.number="state[side].adj.translateX" />
            {{ state[side].adj.translateX }}px
          </label>
          <label>
            Translate Y
            <input type="range" min="-200" max="200" v-model.number="state[side].adj.translateY" />
            {{ state[side].adj.translateY }}px
          </label>
           <label>
             Rotation
             <input type="range" min="-45" max="45" v-model.number="state[side].adj.rotationFine" />
             {{ state[side].adj.rotationOffset + state[side].adj.rotationFine }}°
           </label>
           <div class="rotation-buttons">
             <button class="btn-rotation" @click="rotationOffset(side, -90)">-90°</button>
             <button class="btn-rotation" @click="rotationOffset(side, 90)">+90°</button>
           </div>
          <label>
            Scale
            <input type="range" min="50" max="200" v-model.number="state[side].adj.scale" />
            {{ (state[side].adj.scale / 100).toFixed(2) }}×
          </label>
          <label class="flip-label">
            <input type="checkbox" v-model="state[side].adj.flipH" />
            Flip horizontal
          </label>
          <button class="btn small" @click="resetAdj(side)">Reset</button>
        </div>
      </div>
    </div>

    <!-- Camera selection modal -->
    <div v-if="state.showCameraModal" class="camera-modal-overlay">
      <div class="camera-modal">
        <h3>Select Camera</h3>
        <div class="camera-options">
          <label v-for="camera in state.availableCameras" :key="camera.deviceId" class="camera-option">
            <input type="radio" :value="camera.deviceId" v-model="state.selectedCameraId" />
            {{ camera.label || `Camera ${state.availableCameras.indexOf(camera) + 1}` }}
          </label>
        </div>
        <div class="camera-modal-actions">
          <button class="btn" @click="confirmCameraSelection">Use Camera</button>
          <button class="btn secondary" @click="cancelCameraSelection">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useWebRTC } from '../../composables/useWebRTC.js'

const props = defineProps({ 
  token: String,
  peer_id: String 
})

// ─── Initialize WebRTC ────────────────────────────────────────────────────────
const myPeerId = props.peer_id
const { localStream, streams, remotePeerIds, toggleCamera, getAvailableCameras, startPolling, cleanup } = useWebRTC(props.token, myPeerId)

// ─── Storage helpers ──────────────────────────────────────────────────────────
const defaultAdj = () => ({ translateX: 0, translateY: 0, rotationOffset: 0, rotationFine: 0, scale: 100, flipH: false })
const storageKey = (side) => `hammershark:${props.token}:adj:${side}`
const sourceKey  = (side) => `hammershark:${props.token}:src:${side}`

function loadAdj(side) {
  try {
    const raw = localStorage.getItem(storageKey(side))
    return raw ? { ...defaultAdj(), ...JSON.parse(raw) } : defaultAdj()
  } catch { return defaultAdj() }
}

// ─── Central state object ──────────────────────────────────────────────────────
const state = reactive({
  // Panel state
  left: {
    source: localStorage.getItem(sourceKey('left')) || '',
    paused: false,
    adj: loadAdj('left'),
  },
  right: {
    source: localStorage.getItem(sourceKey('right')) || '',
    paused: false,
    adj: loadAdj('right'),
  },
  
  // UI toggles
  showAdjust: false,
  
  // Camera selection
  showCameraModal: false,
  availableCameras: [],
  selectedCameraId: null,
})

const videoRefs = { left: null, right: null }

// ─── Video refs ───────────────────────────────────────────────────────────────
function setVideoRef(side, el) {
  videoRefs[side] = el
  if (el) nextTick(() => bindPanelVideo(side))
}

// ─── Adjustment management ────────────────────────────────────────────────────
function resetAdj(side) {
  Object.assign(state[side].adj, defaultAdj())
}

function rotationOffset(side, offset) {
  state[side].adj.rotationOffset = (state[side].adj.rotationOffset + offset) % 360
  if (state[side].adj.rotationOffset < 0) {
    state[side].adj.rotationOffset += 360
  }
}

watch(() => ({ ...state.left.adj }),  (v) => localStorage.setItem(storageKey('left'),  JSON.stringify(v)), { deep: true })
watch(() => ({ ...state.right.adj }), (v) => localStorage.setItem(storageKey('right'), JSON.stringify(v)), { deep: true })
watch(() => state.left.source,   (v) => localStorage.setItem(sourceKey('left'),  v))
watch(() => state.right.source,  (v) => localStorage.setItem(sourceKey('right'), v))

// ─── Video element → stream binding ───────────────────────────────────────────
async function bindPanelVideo(side) {
  const el     = videoRefs[side]
  const src    = state[side].source
  const stream = src ? (src === myPeerId ? localStream.value : streams.value[src]) : null
  
  if (!el) return
  if (el.srcObject !== stream) {
    el.srcObject = stream
  }
  if (stream) {
    try {
      await el.play()
      state[side].paused = false
    } catch (e) {
      state[side].paused = true
    }
  } else {
    state[side].paused = false
  }
}

async function playPanel(side) {
  const el = videoRefs[side]
  if (!el) return
  try {
    await el.play()
    state[side].paused = false
  } catch (e) {
    // Autoplay still blocked
  }
}

// ─── Watch panel source changes ────────────────────────────────────────────────
watch(() => state.left.source,  () => bindPanelVideo('left'))
watch(() => state.right.source, () => bindPanelVideo('right'))

// Watch the streams and local stream so that when a stream arrives for the currently
// selected source (e.g. camera starts after dropdown is already set), we rebind.
watch([() => localStream, () => Object.keys(streams)], () => {
  bindPanelVideo('left')
  bindPanelVideo('right')
})

// ─── CSS transform ────────────────────────────────────────────────────────────
function videoStyle(side) {
  const a    = state[side].adj
  const flip = a.flipH ? ' scaleX(-1)' : ''
  const totalRotation = a.rotationOffset + a.rotationFine
  return {
    transform: `translate(${a.translateX}px, ${a.translateY}px) rotate(${totalRotation}deg) scale(${a.scale / 100})${flip}`,
  }
}

// ─── Camera selection ─────────────────────────────────────────────────────────
async function handleShareCamera() {
  if (localStream.value) {
    // Already sharing, stop camera
    toggleCamera()
    return
  }

  // Get available cameras
  const cameras = await getAvailableCameras()
  
  if (cameras.length === 0) {
    alert('No cameras found')
    return
  }
  
  if (cameras.length === 1) {
    // Only one camera, use it directly
    toggleCamera(cameras[0].deviceId)
  } else {
    // Multiple cameras, show selection modal
    state.availableCameras = cameras
    state.selectedCameraId = cameras[0].deviceId
    state.showCameraModal = true
  }
}

function confirmCameraSelection() {
  if (state.selectedCameraId) {
    toggleCamera(state.selectedCameraId)
  }
  state.showCameraModal = false
}

function cancelCameraSelection() {
  state.showCameraModal = false
}

// ─── UI toggles ───────────────────────────────────────────────────────────────
function toggleAdjust() { state.showAdjust = !state.showAdjust }

// ─── Lifecycle ────────────────────────────────────────────────────────────────
onMounted(() => {
  // Rebind videos in case streams already exist
  nextTick(() => {
    bindPanelVideo('left')
    bindPanelVideo('right')
  })

  startPolling()
})

onUnmounted(() => {
  cleanup()
})
</script>

<style scoped>
.hammershark-root {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #111;
  color: #eee;
  overflow: hidden;
  position: fixed;
  inset: 0;
  z-index: 9999;
}

/* Fullscreen overlay controls */
.fs-controls {
  position: absolute;
  top: 0.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 10;
  align-items: center;
}

.peer-id-display {
  background: rgba(0, 0, 0, 0.6);
  color: #999;
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-family: monospace;
  border: 1px solid #444;
}

/* Button overrides */
.btn.active { background: #b91c1c; border-color: #ef4444; }
.btn.small { margin-top: 0.5rem; }

/* Split screen */
.split-screen {
  display: flex;
  flex: 1;
  overflow: hidden;
}
.panel {
  flex: 1;
  position: relative;
  overflow: hidden;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.panel + .panel {
  border-left: 2px solid #333;
}

.source-select-wrap {
  position: absolute;
  top: 0.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
}
.source-select {
  background: rgba(0,0,0,0.7);
  color: #eee;
  border: 1px solid #555;
  border-radius: 4px;
  padding: 0.2rem 0.4rem;
  font-size: 0.8rem;
}

.panel-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform-origin: center center;
}

.play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.55);
  cursor: pointer;
  z-index: 4;
}
.play-icon {
  font-size: 4rem;
  color: #fff;
  line-height: 1;
}
.play-label {
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: #ccc;
}

/* Adjustment panel */
.adjust-panel {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: #1a1a1a;
  border-top: 1px solid #333;
  padding: 0.75rem 1rem;
  max-height: 40vh;
  overflow-y: auto;
  z-index: 20;
}
.adjust-inner {
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
}
.adjust-side {
  flex: 1;
  min-width: 220px;
}
.adjust-side h4 {
  margin: 0 0 0.5rem;
  font-weight: bold;
  font-size: 0.9rem;
  text-transform: capitalize;
}
.adjust-side label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  margin-bottom: 0.35rem;
  white-space: nowrap;
}
.adjust-side label input[type="range"] {
  flex: 1;
  min-width: 80px;
}
.flip-label {
  gap: 0.4rem;
}

.rotation-buttons {
  display: flex;
  gap: 0.25rem;
  margin-top: 0.35rem;
}

.btn-rotation {
  flex: 1;
  padding: 0.3rem 0.5rem;
  font-size: 0.7rem;
  background: #333;
  border: 1px solid #555;
  color: #eee;
  border-radius: 3px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-rotation:hover {
  background: #444;
  border-color: #666;
}

.btn-rotation:active {
  background: #555;
}

/* Camera selection modal */
.camera-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.camera-modal {
  background: #1a1a1a;
  border: 1px solid #555;
  border-radius: 8px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
}

.camera-modal h3 {
  margin: 0 0 1.5rem;
  font-size: 1.25rem;
  color: #f97316;
}

.camera-options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.camera-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.2s;
}

.camera-option:hover {
  background: rgba(249, 115, 22, 0.1);
}

.camera-option input[type="radio"] {
  cursor: pointer;
}

.camera-modal-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.btn.secondary {
  background: #555;
  border-color: #666;
}

.btn.secondary:hover {
  background: #666;
}
</style>
